import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { useStudents } from '../../contexts/StudentsContext';
import { GENDER_OPTIONS, type Gender, type StudentFormValues, emptyStudentForm } from '../../types/student';
import { describeSupabaseError } from '../../utils/supabase';
const UNSPECIFIED = 'unspecified';
export function StudentFormModal() {
  const {
    formOpen,
    editing,
    closeForm,
    createStudent,
    updateStudent
  } = useStudents();
  const [values, setValues] = useState<StudentFormValues>(emptyStudentForm);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!formOpen) return;
    if (editing) {
      setValues({
        fullNames: editing.fullNames,
        gender: editing.gender,
        className: editing.className,
        fatherName: editing.fatherName,
        fatherPhone: editing.fatherPhone,
        motherName: editing.motherName,
        motherPhone: editing.motherPhone,
        note: editing.note
      });
    } else {
      setValues(emptyStudentForm);
    }
  }, [formOpen, editing]);
  const isEmptyRecord = useMemo(() => Object.values(values).every(value => !String(value).trim()), [values]);
  const update = (key: keyof StudentFormValues, value: string) => setValues(current => ({
    ...current,
    [key]: value
  }));
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isEmptyRecord && !editing) {
      toast.warning('Add at least one detail before saving.');
      return;
    }
    setSaving(true);
    try {
      const label = values.fullNames.trim() || 'Untitled record';
      if (editing) {
        await updateStudent(editing, values);
        toast.success('Student record updated', {
          description: label
        });
      } else {
        await createStudent(values);
        toast.success('Student saved to Supabase', {
          description: label
        });
      }
      closeForm();
    } catch (error) {
      toast.error('Could not save the record', {
        description: describeSupabaseError(error)
      });
    } finally {
      setSaving(false);
    }
  };
  return <Dialog open={formOpen} onOpenChange={open => open ? undefined : closeForm()} data-id="element-283" data-mp="11soxxu5214q1">
      <DialogContent className="glass-strong max-h-[92vh] max-w-2xl overflow-y-auto scrollbar-slim rounded-3xl" data-id="element-284" data-mp="16jnix70zmqzm">
        <DialogHeader data-id="element-285" data-mp="1bi8hl20ld2ly">
          <DialogTitle className="flex items-center gap-2 text-lg" data-id="element-286" data-mp="1biqpivwm959p">
            <span className="grid h-9 w-9 place-items-center rounded-xl brand-gradient text-white" data-id="element-287" data-mp="1fln142nqo145">
              <UserPlus className="h-4 w-4" data-id="element-288" data-mp="1kj6l7j74b840" />
            </span>
            {editing ? 'Edit student record' : 'Add a new student'}
          </DialogTitle>
          <DialogDescription data-id="element-289" data-mp="34zmls44tdi4s">
            Every field is optional — save what you have now and complete the rest later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2" data-id="element-290" data-mp="2w1ud3itll8qd">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-291" data-mp="2r2zpm3koqvzx">
            <div className="space-y-2 sm:col-span-2" data-id="element-292" data-mp="2r4khflfm80u3">
              <Label htmlFor="fullNames" data-id="element-293" data-mp="3nrfgq43g1df5">Full names</Label>
              <Input id="fullNames" value={values.fullNames} onChange={event => update('fullNames', event.target.value)} placeholder="e.g. Aline Uwase" className="h-10 rounded-xl" autoComplete="off" data-id="element-294" data-mp="3jqvkd2qp2du2" />
            </div>

            <div className="space-y-2" data-id="element-295" data-mp="bc52jhzxg93h">
              <Label htmlFor="className" data-id="element-296" data-mp="bd6dlmgy0vl7">Class</Label>
              <Input id="className" value={values.className} onChange={event => update('className', event.target.value)} placeholder="e.g. Primary 4B" className="h-10 rounded-xl" autoComplete="off" data-id="element-297" data-mp="6k3fd1180xyo" />
            </div>

            <div className="space-y-2" data-id="element-298" data-mp="3db4vtpt3353y">
              <Label htmlFor="gender" data-id="element-299" data-mp="3i8dctn2hx33m">Gender</Label>
              <Select value={values.gender || UNSPECIFIED} onValueChange={next => update('gender', (next === UNSPECIFIED ? '' : next) as Gender)} data-id="element-300" data-mp="3i96mavsua6pg">
                <SelectTrigger id="gender" className="h-10 w-full rounded-xl" data-id="element-301" data-mp="dtx2jjf65wu0">
                  <SelectValue placeholder="Select gender" data-id="element-302" data-mp="1z17urp958e" />
                </SelectTrigger>
                <SelectContent data-id="element-303" data-mp="51bky6n9xw6r">
                  <SelectItem value={UNSPECIFIED} data-id="element-304" data-mp="50jyblbnn8sm">Not specified</SelectItem>
                  {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-305" data-mp="xiflhj9l0e09">
                      {gender}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="element-306" data-mp="12vw1ayinawxc">
              <Label htmlFor="fatherName" data-id="element-307" data-mp="12uuooplze2di">Father&apos;s name</Label>
              <Input id="fatherName" value={values.fatherName} onChange={event => update('fatherName', event.target.value)} placeholder="Father's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-308" data-mp="1gnki3f3ua9kn" />
            </div>

            <div className="space-y-2" data-id="element-309" data-mp="jycttzbu5md1">
              <Label htmlFor="fatherPhone" data-id="element-310" data-mp="xwjiqpyt4c0k">Father&apos;s telephone</Label>
              <Input id="fatherPhone" type="tel" value={values.fatherPhone} onChange={event => update('fatherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-311" data-mp="xughf9s7ewgj" />
            </div>

            <div className="space-y-2" data-id="element-312" data-mp="2cc9piem9g9qd">
              <Label htmlFor="motherName" data-id="element-313" data-mp="2cfe2a0dskdsn">Mother&apos;s name</Label>
              <Input id="motherName" value={values.motherName} onChange={event => update('motherName', event.target.value)} placeholder="Mother's full name" className="h-10 rounded-xl" autoComplete="off" data-id="element-314" data-mp="2gdlj0v899f3u" />
            </div>

            <div className="space-y-2" data-id="element-315" data-mp="1snhdujff57aq">
              <Label htmlFor="motherPhone" data-id="element-316" data-mp="1xnnbqhca7q19">Mother&apos;s telephone</Label>
              <Input id="motherPhone" type="tel" value={values.motherPhone} onChange={event => update('motherPhone', event.target.value)} placeholder="+250 7xx xxx xxx" className="h-10 rounded-xl" autoComplete="off" data-id="element-317" data-mp="22nrs87d6wkrh" />
            </div>

            <div className="space-y-2 sm:col-span-2" data-id="element-318" data-mp="1ew2w0fx8zc59">
              <Label htmlFor="note" data-id="element-319" data-mp="17eo5lvggcvhn">Small note</Label>
              <Textarea id="note" value={values.note} onChange={event => update('note', event.target.value)} placeholder="Allergies, transport arrangement, anything worth remembering…" rows={3} className="rounded-xl" data-id="element-320" data-mp="17cbnniap2l4l" />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--glass-hairline)] pt-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-321" data-mp="ogbwuatgdnml">
            <p className="text-xs text-muted-foreground" data-id="element-322" data-mp="epqo3uk2f2py">Partial records are saved cleanly — nothing is required.</p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row" data-id="element-323" data-mp="9r5z6ra9k5gt">
              <Button type="button" variant="ghost" className="h-10 rounded-xl" onClick={closeForm} disabled={saving} data-id="element-324" data-mp="9ot5xhz228og">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-6 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-325" data-mp="r970foo0oh0">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-326" data-mp="1tje5p4d5n5we" /> : <Check className="mr-2 h-4 w-4" data-id="element-327" data-mp="1eng5nb041hew" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Submit record'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
}